import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import Paper from "@mui/material/Paper";
import { useConfirm } from "../../common/use-confirm";
import {
  any,
  append,
  difference,
  includes,
  lensProp,
  map,
  mergeRight,
  pipe,
  prop,
  propEq,
  reject,
  set,
  sortBy,
  union,
  without,
} from "ramda";
import { EFIGS_ORDER, prependStipEFIGS } from "../../util/vocabUtils";
import { List } from "react-window";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from "@dnd-kit/modifiers";
import {
  mapOrder,
  reorderItems,
} from "../../creator-application/common/dnd/dndUtils";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripLines } from "@fortawesome/pro-regular-svg-icons";

import Grid from "@mui/material/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import DialogContentText from "@mui/material/DialogContentText";

import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import Typography from "@mui/material/Typography";

import SortableItem from "../../creator-application/common/dnd/SortableItem";

export const sortLanguages = sortBy(prop("order"));
export const setOrder = mapIndexed((val, idx) =>
  set(lensProp("order"), idx, val),
);
const setAvailableOrder = mapIndexed((val, idx) => {
  if (includes(val.id, ["eng", "fre", "ita", "ger", "spa"])) {
    return set(lensProp("order"), EFIGS_ORDER[val.id], val);
  } else {
    return set(lensProp("order"), idx + 5, val);
  }
});

export const setLanguages = pipe(setOrder, sortLanguages);
const setAvailableLanguages = pipe(setAvailableOrder, sortLanguages);

const useStyles = makeStyles(theme => ({
  handle: {
    padding: 12,
    cursor: "grab",
    borderRadius: 5,
    "&:hover": {
      background: theme.palette.secondary.light,
    },
  },

  handleDragging: {
    cursor: "grabbing",
    background: theme.palette.secondary.light,
  },
}));

const SelectedLanguage = React.memo(function SelectedLanguage(props) {
  const {
    language,
    rightChecked,
    handleToggleOptionRight,
    isDragging,
    listeners,
  } = props;
  const classes = useStyles();

  return (
    <ListItem
      role="listitem"
      ContainerComponent="div"
      onClick={handleToggleOptionRight(language)}
    >
      <ListItemIcon>
        <Checkbox
          color="primary"
          checked={includes(language, rightChecked)}
          disableRipple
          inputProps={{
            "aria-labelledby": `selected-item-${language.order}-label`,
          }}
        />
      </ListItemIcon>
      <ListItemText
        id={`selected-item-${language.order}-label`}
        primary={language.name}
      />
      <ListItemSecondaryAction
        className={clsx(classes.handle, {
          [classes.handleDragging]: isDragging,
        })}
        {...listeners}
      >
        <FontAwesomeIcon icon={faGripLines} />
      </ListItemSecondaryAction>
    </ListItem>
  );
});

const SortableLanguage = props => {
  return (
    <SortableItem
      id={props.language.code}
      key={props.language.code}
      virtualizedStyle={props.virtualizedStyle}
      renderChild={({ ...sortableProps }) => (
        <SelectedLanguage
          {...sortableProps}
          language={props.language}
          handleToggleOptionRight={props.handleToggleOptionRight}
          rightChecked={props.rightChecked}
        />
      )}
    />
  );
};

const ROW_HEIGHT = 50; // px

//-- Module scope: rowComponent identity must be stable or List remounts
//-- every row on each render.
function AvailableLanguageRow(props) {
  const {
    index,
    style,
    ariaAttributes,
    itemData,
    leftChecked,
    handleToggleOptionLeft,
  } = props;
  const language = itemData[index];
  return (
    <ListItem
      style={style}
      {...ariaAttributes}
      onClick={handleToggleOptionLeft(language)}
    >
      <ListItemIcon>
        <Checkbox
          color="primary"
          checked={includes(language, leftChecked)}
          tabIndex={-1}
          disableRipple
          inputProps={{
            "aria-labelledby": `available-item-${language.order}-label`,
          }}
        />
      </ListItemIcon>
      <ListItemText
        id={`available-item-${language.order}-label`}
        primary={language.name}
      />
    </ListItem>
  );
}

function SelectedLanguageRow(props) {
  const { index, itemData, rightChecked, handleToggleOptionRight } = props;
  return (
    <SortableLanguage
      language={itemData[index]}
      handleToggleOptionRight={handleToggleOptionRight}
      rightChecked={rightChecked}
      //-- react-window positions rows with a transform, but SortableItem
      //-- merges virtualizedStyle over dnd-kit's drag transform, so pass a
      //-- top-based position instead of the row's style prop.
      virtualizedStyle={{
        position: "absolute",
        top: index * ROW_HEIGHT,
        left: 0,
        width: "100%",
        height: ROW_HEIGHT,
      }}
    />
  );
}

const ProjectLanguagesDialog = ({
  open,
  onCancel,
  onApply,
  project,
  languages, // from API
}) => {
  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (notBlank(project.languages)) {
      const selected = map(
        lang => mergeRight(lang, { id: lang.code }),
        project.languages,
      );
      setSelected(sortLanguages(selected));

      const unused = reject(
        lang => any(propEq("code", lang.id), project.languages),
        prependStipEFIGS(languages),
      );

      setAvailable(setAvailableLanguages(unused));
    } else {
      const available = prependStipEFIGS(languages);
      setAvailable(setAvailableLanguages(available));
    }
  }, [languages, project.languages]);

  const [leftChecked, setLeftChecked] = useState([]);
  const [rightChecked, setRightChecked] = useState([]);
  const handleToggleOptionLeft = useCallback(
    lang => event => {
      if (event.target.checked) {
        setLeftChecked(prev => append(lang, prev));
      } else {
        setLeftChecked(prev => without([lang], prev));
      }
    },
    [],
  );

  const handleToggleOptionRight = useCallback(
    lang => event => {
      if (event.target.checked) {
        setRightChecked(prev => append(lang, prev));
      } else {
        setRightChecked(prev => without([lang], prev));
      }
    },
    [],
  );

  const handleCheckedRight = useCallback(() => {
    setSelected(prev => setLanguages(union(prev, leftChecked)));
    setAvailable(setAvailableLanguages(without(leftChecked, available)));
    setLeftChecked([]);
  }, [leftChecked, available]);

  const handleCheckedLeft = useCallback(() => {
    setAvailable(prev =>
      setLanguages(setAvailableLanguages(union(prev, rightChecked))),
    );
    setSelected(setLanguages(without(rightChecked, selected)));
    setRightChecked([]);
  }, [rightChecked, selected]);

  const handleAllRight = useCallback(() => {
    setSelected(prev => setLanguages(union(prev, available)));
    setAvailable([]);
    setLeftChecked([]);
  }, [available]);

  const handleAllLeft = useCallback(() => {
    setAvailable(prev =>
      setLanguages(setAvailableLanguages(union(prev, selected))),
    );
    setSelected([]);
    setRightChecked([]);
  }, [selected]);

  const handleClose = useCallback(() => {
    const previous = notBlank(project.languages) ? project.languages : [];
    if (isBlank(difference(previous, selected))) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have selected languages without applying.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [selected, project.languages, showConfirm, onCancel]);

  const handleApply = useCallback(() => {
    onApply(selected);
  }, [onApply, selected]);

  //-- Manage Drag and Drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSelected(reorderItems({ active, over, mapper: mapOrder }));
    }
  };

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle id="add-submission-types-dialog-title">
        Update Project Language Options
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Check the Languages that should be allowed for this Project.
        </DialogContentText>
        <Paper>
          <Box display="flex" alignItems="flex-start" mt={3}>
            <Box>
              <Typography variant="h5">Available Languages</Typography>
              <Box mt={1} boxShadow={5}>
                <List
                  rowComponent={AvailableLanguageRow}
                  rowCount={available.length}
                  rowHeight={ROW_HEIGHT}
                  rowProps={{
                    itemData: available,
                    leftChecked,
                    handleToggleOptionLeft,
                  }}
                  style={{ height: 600, width: 350 }}
                />
              </Box>
            </Box>

            <Box height={600} display="flex" alignItems="center" ml={5} mr={5}>
              <Grid
                container
                spacing={2}
                direction="column"
                alignItems="center"
              >
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleAllRight}
                    disabled={available.length === 0}
                    aria-label="move all right"
                  >
                    <LastPageIcon fontSize="large" />
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleCheckedRight}
                    disabled={leftChecked.length === 0}
                    aria-label="move selected right"
                  >
                    <KeyboardArrowRightIcon fontSize="large" />
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleCheckedLeft}
                    disabled={rightChecked.length === 0}
                    aria-label="move selected left"
                  >
                    <KeyboardArrowLeftIcon fontSize="large" />
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleAllLeft}
                    disabled={selected.length === 0}
                    aria-label="move all left"
                  >
                    <FirstPageIcon fontSize="large" />
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="h5">Selected Languages</Typography>
              <Box mt={1} boxShadow={5}>
                <DndContext
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[
                    restrictToVerticalAxis,
                    restrictToFirstScrollableAncestor,
                  ]}
                >
                  <SortableContext
                    // For some reason, the items prop for this sortable context requires a primitive data type
                    items={map(prop("id"), selected)}
                    strategy={verticalListSortingStrategy}
                  >
                    <List
                      rowComponent={SelectedLanguageRow}
                      rowCount={selected.length}
                      rowHeight={ROW_HEIGHT}
                      rowProps={{
                        itemData: selected,
                        rightChecked,
                        handleToggleOptionRight,
                      }}
                      style={{ height: 600, width: 350 }}
                    />
                  </SortableContext>
                </DndContext>
              </Box>
            </Box>
          </Box>
        </Paper>
        <ConfirmDialog handler={confirmHandler} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectLanguagesDialog;
